import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { checkSubscription } from '../../utils/checkSubscription';
import { Link, useLocation } from 'react-router-dom';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import logo from '../../assets/calcium-logo.svg'
import { UserContext } from '../../contexts/UserContext';
import { UIContext } from '../../contexts/UIContext';
import {
	MdClose,
	MdMenu,
	MdMail,
	MdNotifications,
	MdPerson,
	MdChevronRight,
	MdWorkspacePremium,
	MdHomeFilled,
	MdDynamicFeed,
	MdGroups,
	MdTravelExplore,
	MdSmartToy,
	MdFitnessCenter,
	MdRestaurant,
	MdConstruction
} from 'react-icons/md';
import { toast } from 'react-toastify';
import axios from 'axios';
import { io } from 'socket.io-client';
import { buildApiBases, encodePathSegment, requestWithApiFallback } from '../../utils/apiRequest';
import findUserRank from '../../utils/findUserRank';
import formatDisplayName from '../../utils/formatDisplayName';
import VerifiedBadge from '../VerifiedBadge/VerifiedBadge';
import { useAuthUser } from '../../hooks/useAuthUser';
import { AppAuthContext } from '../../contexts/AppAuthContext';
import { resolveSocketAuthPayload } from '../../utils/socketAuth';
import { useAuth } from '@clerk/clerk-react';
import { useClerk } from '@clerk/clerk-react';
import style from './NavBar.module.css';

const EMAIL_TEST_PREFIX = '[EMAIL-TEST]';

const NavBar = () => {
	const { user, signOut, isTestAuthEnabled } = useAuthUser();
	const { getToken } = useAuth();
	const clerk = useClerk();
	const { testAuthSession } = useContext(AppAuthContext);
	const { dbUser } = useContext(UserContext);
	useContext(UIContext);
	const [menuOpen, setMenuOpen] = useState(false);
	const [notifCount, setNotifCount] = useState(0);
	const [testEmailSending, setTestEmailSending] = useState(false);
	const socketRef = useRef(null);
	const location = useLocation();
	const API_URL = process.env.REACT_APP_API_URL;
	const requestApi = useCallback((requestFactory) => requestWithApiFallback(API_URL, requestFactory), [API_URL]);
	const socketBaseUrl = useMemo(() => {
		const bases = buildApiBases(API_URL);
		return bases.find(base => !/\/api$/i.test(base)) || bases[0] || '';
	}, [API_URL]);

	useEffect(() => {
		setMenuOpen(false);
	}, [location.pathname]);

	useEffect(() => {
		if (!menuOpen) return;

		document.body.style.overflow = 'hidden';

		const onEscape = (event) => {
			if (event.key === 'Escape') {
				setMenuOpen(false);
			}
		};
		window.addEventListener('keydown', onEscape);
		return () => {
			document.body.style.overflow = '';
			window.removeEventListener('keydown', onEscape);
		};
	}, [menuOpen]);

	useEffect(() => {
		if (!user?.id) {
			setNotifCount(0);
			return;
		}

		requestApi((base) => axios.get(`${base}/notifications/unread-count/${encodePathSegment(user.id)}`))
			.then(res => setNotifCount(res.data.unreadCount || 0))
			.catch(() => {});
	}, [requestApi, user?.id]);

	useEffect(() => {
		if (!user?.id || !socketBaseUrl) return;
		let isCancelled = false;
		let socket = null;

		const incrementOnNew = ({ notification }) => {
			if (notification?.userID === user.id) {
				setNotifCount(prev => prev + 1);
			}
		};

		const syncReadCount = ({ unreadCount }) => {
			if (typeof unreadCount === 'number') {
				setNotifCount(Math.max(0, unreadCount));
			}
		};

		const setupSocket = async () => {
			const authPayload = await resolveSocketAuthPayload({
				getToken,
				testAuthToken: testAuthSession?.token,
				userID: user.id
			});
			if (!authPayload || isCancelled) return;

			socket = io(socketBaseUrl, {
				transports: ['websocket', 'polling'],
				auth: authPayload
			});
			socketRef.current = socket;
			socket.on('notification:new', incrementOnNew);
			socket.on('notification:read', syncReadCount);
			socket.on('notification:all-read', syncReadCount);
		};

		setupSocket();

		return () => {
			isCancelled = true;
			if (socket) {
				socket.off('notification:new', incrementOnNew);
				socket.off('notification:read', syncReadCount);
				socket.off('notification:all-read', syncReadCount);
				socket.disconnect();
			}
			socketRef.current = null;
		};
	}, [getToken, socketBaseUrl, testAuthSession?.token, user?.id]);

	const isActiveRoute = (paths) => paths.some(path => (
		path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)
	));
	const generateRoute = checkSubscription(dbUser) ? '/interests' : '/onboarding';
	const sidebarDisplayName = formatDisplayName(user?.firstName || user?.username || 'Athlete');
	const sidebarProfileImage = user?.imageUrl || user?.profileImageUrl || '';

	const getSidebarLinkClassName = (paths) => (
		`${style.SidebarLink} ${isActiveRoute(paths) ? style.SidebarLinkActive : ''}`
	);

	const sendTestEmail = useCallback(async () => {
		if (testEmailSending) return;
		const to = user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress || user?.email;
		const body = to ? { to } : {};
		console.log(`${EMAIL_TEST_PREFIX} UI: sidebar item clicked`, body.to ? { to } : { to: '(default)' });
		setTestEmailSending(true);
		try {
			console.log(`${EMAIL_TEST_PREFIX} UI: sending POST /emails/send-test-email`);
			const res = await requestApi((base) => axios.post(`${base}/emails/send-test-email`, body));
			console.log(`${EMAIL_TEST_PREFIX} UI: response ok`, res?.data);
			toast.success(res?.data?.message || 'Test email sent');
		} catch (err) {
			console.error(`${EMAIL_TEST_PREFIX} UI: request failed`, err?.response?.data ?? err?.message);
			toast.error(err?.response?.data?.error || err?.message || 'Test email failed');
		} finally {
			setTestEmailSending(false);
		}
	}, [requestApi, user, testEmailSending]);

	// const getUser = useCallback(() => {
	// 	if (user) {
	// 		axios.get(`${API_URL}/users/${user.id}`)
	// 			.then(res => (setDbUser(res.data[0])))
	// 			.catch(err => console.log("Error:", err.message));
	// 	}
	// }, [API_URL, user]);


	return (
		<>
			<nav className={style.NavBar}>
				<Link to='/' className={style.NavLogoLink}>
					<div className={style.NavLogoContainer}>
						<img src={logo} alt="Calcium" className={style.NavBarLogo} />
						<span className={style.UserNavInfo}>
							{checkSubscription(dbUser) ? 'Calcium Plus' : 'Calcium'}
						</span>
					</div>
				</Link>

				<div className={style.NavRight}>
					<ThemeToggle />
					{user && (
						<Link to='/notifications' className={style.NotifBtn} aria-label='Notifications'>
							<MdNotifications />
							{notifCount > 0 && <span className={style.NotifBadge}>{notifCount > 99 ? '99+' : notifCount}</span>}
						</Link>
					)}
					{
						user ?
							<button
								type='button'
								className={style.MenuToggle}
								onClick={() => setMenuOpen(true)}
								aria-label='Open menu'
							>
								<MdMenu />
							</button>
							:
							<Link to='/login'>
								<PrimaryButton text='Log in' click={() => null} />
							</Link>

					}
				</div>
			</nav>

			{menuOpen && user && (
				<>
					<button
						type='button'
						className={style.MenuOverlay}
						aria-label='Close menu overlay'
						onClick={() => setMenuOpen(false)}
					/>
					<aside className={style.Sidebar}>
						<div className={style.SidebarHeader}>
							<div className={style.SidebarUser}>
								<span className={style.SidebarGreeting}>
									Hello, {formatDisplayName(user.username)}
									<VerifiedBadge userPlan={dbUser?.userPlan} totalXP={dbUser?.totalXP} />
									{!checkSubscription(dbUser) && dbUser?.totalXP != null && (
										<span className={style.SidebarRankBadge}>{findUserRank(dbUser.totalXP)?.emoji}</span>
									)}
								</span>
								{checkSubscription(dbUser) ? (
									<span className={style.SidebarPlan}>Calcium Plus</span>
								) : (
									<span className={style.SidebarPlan}>Calcium Free</span>
								)}
							</div>
							<button
								type='button'
								className={style.MenuClose}
								onClick={() => setMenuOpen(false)}
								aria-label='Close menu'
							>
								<MdClose />
							</button>
						</div>

						<div className={style.SidebarActions}>
							<span className={style.SidebarSectionTitle}>Main</span>
							<Link to='/' className={getSidebarLinkClassName(['/'])}>
								<MdHomeFilled />
								<span>Home</span>
							</Link>
							<Link to='/feed' className={getSidebarLinkClassName(['/feed'])}>
								<MdDynamicFeed />
								<span>Community Feed</span>
							</Link>
							<Link to='/explore-routines' className={getSidebarLinkClassName(['/explore-routines', '/stryd'])}>
								<MdTravelExplore />
								<span>Explore Routines</span>
							</Link>
							<Link to='/people' className={getSidebarLinkClassName(['/people', '/profile/'])}>
								<MdGroups />
								<span>People</span>
							</Link>

							<span className={style.SidebarSectionTitle}>Create</span>
							<Link to={generateRoute} className={getSidebarLinkClassName(['/interests', '/generated-stryds', '/onboarding'])}>
								<MdSmartToy />
								<span>{checkSubscription(dbUser) ? 'Generate Routine' : 'Start AI Plan'}</span>
							</Link>
							<Link to='/add-routine' className={getSidebarLinkClassName(['/add-routine'])}>
								<MdFitnessCenter />
								<span>Create Routine</span>
							</Link>
							<Link to='/plan-builder' className={getSidebarLinkClassName(['/plan-builder', '/generated-plan'])}>
								<MdConstruction />
								<span>Build Plan</span>
							</Link>
							<Link to='/meals' className={getSidebarLinkClassName(['/meals'])}>
								<MdRestaurant />
								<span>Meal Plans</span>
							</Link>
							<Link to='/cali-chat' className={getSidebarLinkClassName(['/cali-chat'])}>
								<MdSmartToy />
								<span>Cali AI</span>
							</Link>

							<span className={style.SidebarSectionTitle}>Account</span>
							<Link to='/notifications' className={getSidebarLinkClassName(['/notifications'])}>
								<MdNotifications />
								<span>Notifications</span>
							</Link>
							<Link to='/profile-page' className={getSidebarLinkClassName(['/profile-page'])}>
								<MdPerson />
								<span>Profile</span>
							</Link>
							<Link to='/upgrade' className={getSidebarLinkClassName(['/upgrade', '/plan-confirm'])}>
								<MdWorkspacePremium />
								<span>{checkSubscription(dbUser) ? 'Subscriptions' : 'Upgrade'}</span>
							</Link>
							<button
								type="button"
								className={style.SidebarLink}
								onClick={sendTestEmail}
								disabled={testEmailSending}
							>
								<MdMail />
								<span>{testEmailSending ? 'Sending…' : 'Test email'}</span>
							</button>
						</div>

						<div className={style.SidebarFooter}>
							{isTestAuthEnabled ? (
								<button className={style.SidebarSignOutBtn} onClick={signOut} type='button'>
									Log out
								</button>
							) : (
								<button
									type="button"
									className={style.SidebarAccountButton}
									aria-label="Open account"
									onClick={() => {
										if (clerk?.openUserProfile) {
											clerk.openUserProfile();
											setMenuOpen(false);
										}
									}}
								>
									<span className={style.SidebarAccountAvatar}>
										{sidebarProfileImage ? (
											<img src={sidebarProfileImage} alt={`${sidebarDisplayName} profile`} className={style.SidebarAccountAvatarImage} />
										) : (
											<MdPerson />
										)}
									</span>
									<span className={style.SidebarAccountMeta}>
										<span className={style.SidebarAccountName}>{sidebarDisplayName}</span>
										<span className={style.SidebarAccountHint}>Open account</span>
									</span>
									<MdChevronRight className={style.SidebarAccountChevron} />
								</button>
							)}
						</div>
					</aside>
				</>
			)}
		</>
	)
}

export default NavBar;